import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { useFieldContext } from "./form-context";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { cn } from "@/lib/utils";
import { scrollIntoViewWithOffset } from "@/lib/scroll-into-view-with-offset";

export function FormDatePicker({ label, id, description }: { label: string; id: string; description?: string }) {
  const field = useFieldContext<string>();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const selectedDate = field.state.value ? new Date(field.state.value) : undefined;

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      const localDate = format(date, "yyyy-MM-dd");
      field.handleChange(localDate);
    }
    setPopoverOpen(false);
  };

  const handleClearDate = () => {
    field.handleChange(null as unknown as string);
    setPopoverOpen(false);
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <Label htmlFor={id}>{label}</Label>

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              "justify-start text-left font-normal flex items-center",
              !field.state.value && "text-muted-foreground"
            )}
            onFocus={(e) => scrollIntoViewWithOffset(e.currentTarget)}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            <div className="flex justify-between items-center w-full">
              {selectedDate ? format(selectedDate, "LLL dd, y") : <span>Pick a date</span>}
              {field.state.value && (
                <span
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearDate();
                  }}
                  className="ml-2 cursor-pointer text-sm text-muted-foreground"
                >
                  ✕
                </span>
              )}
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            autoFocus
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today;
            }}
          />
        </PopoverContent>
      </Popover>

      {description && <div className="text-xs form-description">{description}</div>}
    </div>
  );
}
